/**
 * Energy Calculator Module
 * Handles Total Energy Compensated (TEC) altitude and vario calculations
 * Based on extend_spectate_full_features.py TEC calculations
 */

// TEC calculation compensation factor (accounts for air resistance)
// Reduces energy value to account for real-world air resistance
const TEC_COMPENSATION_FACTOR = 0.85;

// Gravity constant
const GRAVITY = 9.81; // m/s²

/**
 * Calculate Total Energy Compensated (TEC) altitude
 * TEC altitude = GPS altitude + kinetic energy (as potential altitude)
 *
 * The 0.7 factor accounts for air resistance when converting speed to altitude
 * Formula: TEC = altitude + 0.7 * (v²/(2g))
 *
 * Based on Python: calculate_tec() lines 1811-1816
 *
 * @param {number} altitude_m - GPS altitude in meters
 * @param {number} speed_kmh - Ground speed in km/h
 * @param {number} tas_kmh - True airspeed in km/h (optional, uses speed_kmh if not provided)
 * @returns {number} TEC altitude in meters
 */
export function calculateTEC(altitude_m, speed_kmh, tas_kmh = null) {
  // Use TAS if available, otherwise use ground speed
  const speed_mps = (tas_kmh !== null ? tas_kmh : speed_kmh) / 3.6;

  // Kinetic energy converted to potential altitude
  const kineticAltitude_m = TEC_COMPENSATION_FACTOR * ((speed_mps * speed_mps) / (2 * GRAVITY));

  return altitude_m + kineticAltitude_m;
}

/**
 * Calculate TEC vario (climb rate)
 * This is the rate of change of TEC altitude
 *
 * @param {number} currentTEC_m - Current TEC altitude in meters
 * @param {number} previousTEC_m - Previous TEC altitude in meters
 * @param {number} deltaTime_s - Time difference in seconds
 * @returns {number} TEC vario in m/s
 */
export function calculateTECVario(currentTEC_m, previousTEC_m, deltaTime_s) {
  if (deltaTime_s <= 0) {
    return 0;
  }

  return (currentTEC_m - previousTEC_m) / deltaTime_s;
}

/**
 * Calculate the kinetic energy component (altitude equivalent)
 *
 * @param {number} speed_kmh - Speed in km/h
 * @returns {number} Altitude equivalent in meters
 */
export function calculateKineticAltitude(speed_kmh) {
  const speed_mps = speed_kmh / 3.6;
  return TEC_COMPENSATION_FACTOR * ((speed_mps * speed_mps) / (2 * GRAVITY));
}

/**
 * Calculate the speed equivalent of a given altitude difference
 * Inverse of kinetic altitude calculation
 *
 * @param {number} altitude_m - Altitude in meters
 * @returns {number} Equivalent speed in km/h
 */
export function altitudeToSpeed(altitude_m) {
  // v² = 2 * g * h / compensation_factor
  const speed_mps = Math.sqrt((2 * GRAVITY * altitude_m) / TEC_COMPENSATION_FACTOR);
  return speed_mps * 3.6;
}

/**
 * Energy Altitude Calculator
 * Manages TEC calculations and history for an aircraft
 */
export class EnergyCalculator {
  constructor() {
    this.tecHistory = new Map(); // cookie -> array of {time, tec, altitude, speed}
    this.maxHistoryTime = 20; // seconds of history to keep
  }

  /**
   * Update TEC for an aircraft and calculate vario
   *
   * @param {string} cookie - Aircraft identifier
   * @param {number} altitude_m - Current GPS altitude
   * @param {number} speed_kmh - Current speed
   * @param {number} timestamp - Current timestamp
   * @param {number} tas_kmh - True airspeed (optional)
   * @returns {object} {tec_m, vario_ms, kinetic_m}
   */
  updateTEC(cookie, altitude_m, speed_kmh, timestamp, tas_kmh = null) {
    const tec_m = calculateTEC(altitude_m, speed_kmh, tas_kmh);
    const kinetic_m = calculateKineticAltitude(tas_kmh || speed_kmh);

    // Get history for this aircraft
    if (!this.tecHistory.has(cookie)) {
      this.tecHistory.set(cookie, []);
    }

    const history = this.tecHistory.get(cookie);

    // Calculate vario if we have previous data
    let vario_ms = 0;
    if (history.length > 0) {
      const previous = history[history.length - 1];
      const deltaTime_s = (timestamp - previous.time) / 1000;

      if (deltaTime_s > 0) {
        vario_ms = calculateTECVario(tec_m, previous.tec, deltaTime_s);
      }
    }

    // Add current point to history
    history.push({
      time: timestamp,
      tec: tec_m,
      altitude: altitude_m,
      speed: speed_kmh,
      vario: vario_ms
    });

    // Trim old history
    const cutoffTime = timestamp - (this.maxHistoryTime * 1000);
    while (history.length > 0 && history[0].time < cutoffTime) {
      history.shift();
    }

    return {
      tec_m,
      vario_ms,
      kinetic_m
    };
  }

  /**
   * Get average TEC vario over a time window
   *
   * @param {string} cookie - Aircraft identifier
   * @param {number} windowSeconds - Time window in seconds
   * @returns {number} Average vario in m/s
   */
  getAverageTECVario(cookie, windowSeconds) {
    const history = this.tecHistory.get(cookie);
    if (!history || history.length < 2) {
      return 0;
    }

    const now = history[history.length - 1].time;
    const cutoffTime = now - (windowSeconds * 1000);

    // Filter to window
    const windowData = history.filter(point => point.time >= cutoffTime);

    if (windowData.length < 2) {
      return 0;
    }

    // Calculate average vario
    let totalVario = 0;
    let count = 0;

    for (const point of windowData) {
      if (point.vario !== undefined) {
        totalVario += point.vario;
        count++;
      }
    }

    return count > 0 ? totalVario / count : 0;
  }

  /**
   * Clear history for an aircraft
   *
   * @param {string} cookie - Aircraft identifier
   */
  clearHistory(cookie) {
    this.tecHistory.delete(cookie);
  }

  /**
   * Clear all history
   */
  clearAllHistory() {
    this.tecHistory.clear();
  }
}
